import { useCallback, useEffect, useMemo, useState } from "react";

import router, { EVENT_LOADING_DATA } from "@/__main__/router.mjs";
import { devDebug, devWarn } from "@/util/dev.mjs";
import globals from "@/util/global-whitelist.mjs";

/**
 * A hook that allows you to safely react to the browser's back button even if
 * multiple components are attempting to do so.
 *
 * This is useful for things like modals or nested navigation, where you want
 * to be able to react to the browser's back button without actually changing
 * the route.
 *
 * When the user makes a "significant change" you should call `claim()` to claim
 * the scope. These calls are added to a stack of entries so that when the user
 * presses the back button, the most recent entry can be popped off the stack
 * and `onPop` can be fired on the proper component. If you no longer need to
 * receive back button events, you should call `clear()` to remove your entries
 * from the stack. If the component is unmounted, then `clear()` will be called
 * automatically. There is no mechanism for clearing your "most recent" claim,
 * since another component may have claimed the scope after you did and it's
 * more important for the behavior to be predictable.
 *
 * Regardless, if app navigation is triggered in any form, all claims will be
 * cleared.
 *
 * For example, if you have a modal that you want to close when the user presses
 * the back button, you should claim the scope when the modal is opened, and
 * clear the scope when the modal is closed. If you have navigation within the
 * modal, you could claim the scope when the user navigates and clear the scope
 * if the navigator is no longer relevant.
 */
export function useSharedScope({
  name,
  onPop,
}: {
  name: string;
  onPop?: () => void;
}): {
  claim: () => void;
  clear: () => void;
} {
  const [uniqueScopeId, setScopeId] = useState(
    () => name + "-" + Math.random().toString(36).slice(2),
  );

  useEffect(() => {
    setScopeId(name + "-" + Math.random().toString(36).slice(2));
  }, [name]);

  useEffect(() => {
    scopePopCallbacks[uniqueScopeId] = onPop;

    return () => {
      delete scopePopCallbacks[uniqueScopeId];
    };
  }, [uniqueScopeId, onPop]);

  const claim = useCallback(() => {
    pushScopeEntry(uniqueScopeId);
  }, [uniqueScopeId]);

  const clear = useCallback(() => {
    removeAllScopeEntries(uniqueScopeId);
  }, [uniqueScopeId]);

  useEffect(() => {
    return () => {
      removeAllScopeEntries(uniqueScopeId);
    };
  }, [uniqueScopeId]);

  return useMemo(() => ({ claim, clear }), [claim, clear]);
}

let currentSharedScopeUid =
  globals.history?.state?.["__shared_scope_uid"] ||
  Number.parseInt(globals.sessionStorage?.getItem("__shared_scope_uid"), 10) ||
  0;

function nextSharedScopeUid() {
  const value = currentSharedScopeUid + 1;
  setCurrentSharedScopeUid(value);
  return value;
}

function setCurrentSharedScopeUid(uid: number) {
  currentSharedScopeUid = uid;

  globals.sessionStorage?.setItem("__shared_scope_uid", uid.toString(10));
}

let scopeStack: string[] = [];
const scopePopCallbacks: { [key: string]: () => void } = {};

let scopeStackHeadUid = null;

// If Chrome detects that the page used `pushState()` after a `popstate` event, it will
// trigger its "back trapping" protection. This will cause the next press of the browser
// back button to go to a new tab page instead of the previous page or triggering `popstate`
// again. To prevent this, we need to keep track of whether the user has recently popped
// a state so that we don't trigger this mechanism.
let hasPoppedStateRecently = false;

globals.addEventListener?.("popstate", (event) => {
  hasPoppedStateRecently = true;

  setTimeout(() => {
    hasPoppedStateRecently = false;
  }, 16);

  const newSharedScopeEntryUid = event.state?.["__shared_scope_entry"] ?? null;
  const previousSharedScopeUid = currentSharedScopeUid;

  if (newSharedScopeEntryUid !== null) {
    setCurrentSharedScopeUid(newSharedScopeEntryUid);
  }

  if (previousSharedScopeUid === newSharedScopeEntryUid) {
    return;
  }

  if (
    newSharedScopeEntryUid === null ||
    newSharedScopeEntryUid < previousSharedScopeUid
  ) {
    const numToRemove = Math.min(
      scopeStack.length,
      previousSharedScopeUid - (newSharedScopeEntryUid ?? 0),
    );

    for (let i = 0; i < numToRemove; i++) {
      const scopeId = scopeStack.pop();

      devDebug('[shared-scope] Pop state for "%s"', scopeId);

      scopePopCallbacks[scopeId]?.();
    }

    if (scopeStack.length === 0) {
      scopeStackHeadUid = null;
    }
  }
});

// Theoretically a click event will clear the "back trapping" protection.
globals.addEventListener?.("click", () => {
  hasPoppedStateRecently = false;
});

router.events.on(EVENT_LOADING_DATA, () => {
  // If the route has changed then it's not safe to clean up history entries
  scopeStackHeadUid = null;

  scopeStack = [];
});

function pushScopeEntry(scopeId: string) {
  scopeStackHeadUid ??= currentSharedScopeUid;

  devDebug('[shared-scope] Pushing scope entry for "%s"', scopeId);

  scopeStack.push(scopeId);

  updateHistoryEntries();
}

function removeAllScopeEntries(scopeId: string) {
  devDebug('[shared-scope] Removing all scope entries for "%s"', scopeId);

  // Remove any leftover references to the unique id in the last changed array.
  scopeStack = scopeStack.filter((id) => id !== scopeId);

  updateHistoryEntries();
}

// It's possible (and likely) that we can get into a situation where we push a
// history entry, and then immediately pop it. This can cause multiple history
// entries to be added to the history without being removed, which can cause
// issues with the back button.
let queuedHistoryModification: Timeout = null;

function updateHistoryEntries() {
  if (queuedHistoryModification !== null) {
    clearTimeout(queuedHistoryModification);
  }

  queuedHistoryModification = setTimeout(() => {
    queuedHistoryModification = null;

    // If we don't know where we started, then we cannot safely add or remove history entries
    if (scopeStackHeadUid === null) {
      return;
    }

    const targetHistoryEntries = scopeStack.length;

    const currentHistoryEntries = currentSharedScopeUid - scopeStackHeadUid;

    if (targetHistoryEntries > currentHistoryEntries) {
      if (hasPoppedStateRecently) {
        devWarn(
          "[shared-scope] Due to a recent popstate event, we are unable to add a new history entry.",
        );

        return;
      }

      const numEntriesMissing = targetHistoryEntries - currentHistoryEntries;

      devDebug(
        "[shared-scope] Pushing",
        numEntriesMissing,
        "history",
        numEntriesMissing === 1 ? "entry" : "entries",
      );

      for (let i = 0; i < numEntriesMissing; i++) {
        globals.history.pushState(
          {
            ...router.route?.state,
            __shared_scope_entry: nextSharedScopeUid(),
          },
          "",
          globals.location.href,
        );
      }
    } else if (targetHistoryEntries < currentHistoryEntries) {
      const numToRemove = currentHistoryEntries - targetHistoryEntries;

      devDebug(
        "[shared-scope] Popping",
        numToRemove,
        "history",
        numToRemove === 1 ? "entry" : "entries",
      );

      globals.history.go(-numToRemove);

      // This is normally set in the `popstate` handler, but it may be fired *after* we have
      // pushed more state if it occurs in the same update, meaning it could end up out of
      // sync resulting in extra history entries being popped on cleanup.
      setCurrentSharedScopeUid(currentSharedScopeUid - numToRemove);
    }
  }, 1);
}
